import { useLayoutEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react'
import { ArrowUp } from 'lucide-react'
import type { LiveConversation } from '@manaro/contracts'
import { ApiError } from '@/shared/api/client.ts'
import { cn } from '@/shared/lib/cn.ts'
import { Button } from '@/shared/ui/button.tsx'
import { useLiveAction, useOperatorReply } from '../use-support-chat.ts'

const MAX_LENGTH = 2000
const MAX_HEIGHT_PX = 160

interface ComposerProps {
  conversation: LiveConversation
  me: string
  onSending: (text: string | null) => void
}

function StatusStrip({ conversation, me }: { conversation: LiveConversation; me: string }) {
  const action = useLiveAction(conversation.id)
  const mine = conversation.status === 'operator' && conversation.operatorEmail === me

  const busy = (kind: 'takeover' | 'release') => action.isPending && action.variables === kind
  const takeover = (label: string, primary: boolean) => (
    <Button size="sm" variant={primary ? 'primary' : 'secondary'} loading={busy('takeover')} onClick={() => action.mutate('takeover')}>
      {label}
    </Button>
  )

  const [text, button, tone] =
    conversation.status === 'waiting_operator'
      ? ['Ждёт оператора — ответьте или заберите разговор', takeover('Взять', true), 'warn']
      : conversation.status === 'operator' && mine
        ? [
            'Вы в разговоре — бот молчит',
            <Button key="release" size="sm" variant="secondary" loading={busy('release')} onClick={() => action.mutate('release')}>
              Вернуть боту
            </Button>,
            'brand',
          ]
        : conversation.status === 'operator'
          ? [`Ведёт ${conversation.operatorName ?? 'другой оператор'}`, takeover('Забрать себе', false), 'neutral']
          : conversation.status === 'closed'
            ? ['Разговор закрыт — ваш ответ откроет его снова', null, 'neutral']
            : ['Отвечает бот — ваш ответ заберёт разговор', takeover('Взять', false), 'neutral']

  return (
    <div
      className={cn(
        'flex min-h-[44px] items-center gap-3 px-4 py-1.5 text-[13px]',
        tone === 'warn' && 'bg-warn-tint text-warn-ink',
        tone === 'brand' && 'bg-brand-tint text-brand-ink',
        tone === 'neutral' && 'text-ink-muted',
      )}
    >
      <span className="min-w-0 flex-1" role={action.error ? 'alert' : undefined}>
        {action.error
          ? action.error instanceof ApiError
            ? action.error.message
            : 'Не получилось — попробуйте ещё раз.'
          : text}
      </span>
      {button}
    </div>
  )
}

/** Messenger-style input: grows with the text, sends with the round button (and Enter on a computer). */
export function Composer({ conversation, me, onSending }: ComposerProps) {
  const [text, setText] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const reply = useOperatorReply(conversation.id)

  useLayoutEffect(() => {
    const element = textareaRef.current
    if (!element) return
    element.style.height = 'auto'
    element.style.height = `${Math.min(element.scrollHeight, MAX_HEIGHT_PX)}px`
  }, [text])

  function submit(event?: FormEvent) {
    event?.preventDefault()
    const value = text.trim()
    if (!value || reply.isPending) return
    setText('')
    onSending(value)
    reply.mutate(value, {
      // Nothing typed meanwhile is lost: the failed message goes back in front of it.
      onError: () => {
        setText((current) => (current.trim() ? `${value}\n${current}` : value))
        onSending(null)
      },
    })
    textareaRef.current?.focus()
  }

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    // Enter that confirms an IME candidate (Safari reports keyCode 229) must not send.
    if (event.key !== 'Enter' || event.nativeEvent.isComposing || event.keyCode === 229) return
    // Phones keep Enter for new lines; on a computer Enter sends and Shift+Enter breaks the line.
    const desktop = window.matchMedia('(pointer: fine)').matches
    if (event.metaKey || event.ctrlKey || (desktop && !event.shiftKey)) {
      event.preventDefault()
      submit()
    }
  }

  return (
    <div className="shrink-0 border-t border-line bg-surface pb-[env(safe-area-inset-bottom)]">
      <StatusStrip conversation={conversation} me={me} />
      {reply.error && (
        <p role="alert" className="px-4 pb-1 text-[13px] text-warn-ink">
          {reply.error instanceof ApiError ? reply.error.message : 'Сообщение не ушло — проверьте связь и отправьте ещё раз.'}
        </p>
      )}
      <form onSubmit={submit} className="flex items-end gap-2 px-3 pt-1 pb-2.5">
        <textarea
          ref={textareaRef}
          value={text}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={onKeyDown}
          rows={1}
          maxLength={MAX_LENGTH}
          enterKeyHint="enter"
          placeholder="Сообщение"
          aria-label="Ответ посетителю"
          className="max-h-[160px] min-h-[44px] flex-1 resize-none rounded-[22px] border border-line-strong bg-surface-soft px-4 py-[10px] text-[16px] leading-[1.4] text-ink outline-none placeholder:text-ink-faint focus:border-brand"
        />
        <button
          type="submit"
          disabled={!text.trim() || reply.isPending}
          aria-label="Отправить"
          className="flex size-11 shrink-0 items-center justify-center rounded-full bg-brand text-white transition-opacity disabled:opacity-40"
        >
          <ArrowUp className="size-5" strokeWidth={2.4} />
        </button>
      </form>
    </div>
  )
}
